import React, { useState } from "react";
import { Mail, Phone, MapPin, Globe2, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COMPANY, SERVICES } from "@/data/content";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { trackFormSubmit, trackFormStart, trackEmailClick } from "@/lib/analytics";

export default function Contact() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", company: "", phone: "", state: "",
    service_interest: "", message: "", company_website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [started, setStarted] = useState(false);

  useSEO({
    title: "Contact Wasserlab",
    description: "Contact Wasserlab for remote Tier II, EPCRA and environmental compliance support for U.S. industrial facilities.",
    path: "/contact",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads/contact", form);
      trackFormSubmit("contact");
      setDone(true);
      toast.success("Message sent. Our compliance team will contact you.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="relative overflow-hidden bg-navy-900 noise-overlay">
        <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-16 lg:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Contact</p>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            Let's Discuss Your Tier II Reporting Requirements
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">
            Tell us about your facility and reporting needs. Our compliance team typically responds within one business
            day. Your information is kept confidential and used only to respond to your request.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-bold text-navy-900">Wasserlab</h2>
            <p className="mt-1 text-sm font-semibold text-emerald-600">{COMPANY.usServices}</p>

            <div className="mt-8 space-y-6">
              <div className="flex items-start gap-4" data-testid="contact-email-block">
                <Mail className="mt-1 h-5 w-5 shrink-0 text-navy-900" aria-hidden="true" />
                <div>
                  <p className="text-sm font-bold text-navy-900">Email</p>
                  <a href={`mailto:${COMPANY.email}`} onClick={() => trackEmailClick("contact")} className="text-sm text-slate-600 hover:text-amber-600 transition-colors">{COMPANY.email}</a>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-phone-block">
                <Phone className="mt-1 h-5 w-5 shrink-0 text-navy-900" aria-hidden="true" />
                <div>
                  <p className="text-sm font-bold text-navy-900">Phone</p>
                  <p className="text-sm text-slate-600">{COMPANY.usPhone || "Phone number coming soon"}</p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-address-block">
                <MapPin className="mt-1 h-5 w-5 shrink-0 text-navy-900" aria-hidden="true" />
                <div>
                  <p className="text-sm font-bold text-navy-900">India Delivery Center</p>
                  <p className="text-sm text-slate-600">{COMPANY.indiaAddress}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Globe2 className="mt-1 h-5 w-5 shrink-0 text-navy-900" aria-hidden="true" />
                <div>
                  <p className="text-sm font-bold text-navy-900">Delivery Model</p>
                  <p className="text-sm text-slate-600">Remote support delivered by a specialized international team.</p>
                </div>
              </div>
            </div>

            <p className="mt-8 rounded-lg border border-slate-200 bg-slate-50 p-4 text-xs leading-relaxed text-slate-600">
              Wasserlab does not maintain a physical U.S. office. Any U.S. mailing address, when configured, is a business
              mailing address and is not represented as a physical office location.
            </p>
          </div>

          <div className="lg:col-span-3">
            {done ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-10 text-center" data-testid="contact-success">
                <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" aria-hidden="true" />
                <h3 className="mt-4 font-heading text-2xl font-bold text-navy-900">Thank You — Your Request Has Been Received</h3>
                <p className="mt-2 text-slate-600">We will review your information and contact you regarding your Tier II reporting requirements.</p>
              </div>
            ) : (
              <form onSubmit={submit} onFocus={() => { if (!started) { setStarted(true); trackFormStart("contact"); } }} className="rounded-xl border border-slate-200 bg-slate-50 p-6 lg:p-8 space-y-5" data-testid="contact-form">
                <input type="text" value={form.company_website} onChange={set("company_website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="c-first">First Name *</Label>
                    <Input id="c-first" required value={form.first_name} onChange={set("first_name")} className="mt-1.5 bg-white" data-testid="contact-first-name" />
                  </div>
                  <div>
                    <Label htmlFor="c-last">Last Name</Label>
                    <Input id="c-last" value={form.last_name} onChange={set("last_name")} className="mt-1.5 bg-white" data-testid="contact-last-name" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="c-email">Business Email *</Label>
                    <Input id="c-email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 bg-white" data-testid="contact-email" />
                  </div>
                  <div>
                    <Label htmlFor="c-company">Company</Label>
                    <Input id="c-company" value={form.company} onChange={set("company")} className="mt-1.5 bg-white" data-testid="contact-company" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="c-phone">Phone</Label>
                    <Input id="c-phone" value={form.phone} onChange={set("phone")} className="mt-1.5 bg-white" data-testid="contact-phone" />
                  </div>
                  <div>
                    <Label>Service Interest</Label>
                    <Select value={form.service_interest} onValueChange={(v) => setForm((f) => ({ ...f, service_interest: v }))}>
                      <SelectTrigger className="mt-1.5 bg-white" data-testid="contact-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
                      <SelectContent>
                        {SERVICES.map((s) => <SelectItem key={s.slug} value={s.name}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="c-message">Message *</Label>
                  <Textarea id="c-message" required rows={5} value={form.message} onChange={set("message")} className="mt-1.5 bg-white" data-testid="contact-message" />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-amber-500 px-6 py-3.5 text-base font-bold text-navy-900 transition-transform hover:-translate-y-0.5 hover:bg-amber-400 disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {submitting && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
